import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {
    Alert,
    Box,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
    Tab,
    TextField
} from "@mui/material";
import {TabContext} from "@mui/lab";
import {useState} from "react";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";

import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from '@mui/icons-material/Check';
import PaidIcon from '@mui/icons-material/Paid';
import GppBadIcon from '@mui/icons-material/GppBad';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import PrintIcon from '@mui/icons-material/Print';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import UndoIcon from '@mui/icons-material/Undo';
import {FolioService} from "crqlar-react-common/dist/services/billing/folio.service";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import SettingsEntityHeader from "../../layout/SettingsEntityHeader";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import Property from "crqlar-react-common/dist/components/Property";
import ScrollableTabList from "crqlar-react-common/dist/components/tabs/ScrollableTabList";
import {
    canCapturePreauthorizedFolio,
    canEditFolioItems,
    canFinalizeFolio,
    canMarkAsPaid,
    canMarkAsUncollectable,
    canRefund,
    canReleaseFunds,
    canUpdateFolio,
    canVoidFolio
} from "crqlar-react-common/dist/utils/folio.util";
import {useQuery} from "react-query";
import {FolioItemService} from "crqlar-react-common/dist/services/billing/folioItem.service";
import {Folio, FolioLog, FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import FolioItemDialog from "crqlar-react-common/dist/components/FolioItemDialog";
import FolioItemRow, {
    FolioItemsHeader,
    getFolioItemDescription,
    getFolioItemGridColumns,
    hasFolioItemDiscount,
    hasFolioItemTax
} from "crqlar-react-common/dist/components/FolioItemRow";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import FolioChargeDialog from "./FolioChargeDialog";
import FolioPaymentDetailsDialog from "./FolioPaymentDetailsDialog";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {PaymentMethodService} from "crqlar-react-common/dist/services/billing/paymentMethod.service";
import {formatDateHM} from "crqlar-react-common/dist/utils/date.util";
import {useDispatch} from "react-redux";
import {useReservations} from "crqlar-react-common/dist/utils/hooks/reservations/useReservationsSchedule";
import FolioBasicInfo from "./FolioBasicInfo";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

const FolioDetails = () => {

    //region State

    useTrackScreenVisit('system folio details')

    const readOnlyUser = useReadOnly()

    const {t} = useTranslation()
    const {id} = useParams()
    const organization = useOrganization()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [currentFolioItem, setCurrentFolioItem] = useState<FolioItem>()
    const [showChargeDialog, setShowChargeDialog] = useState(false)
    const [showLogsDialog, setShowLogsDialog] = useState(false)
    const [currentPaymentLogs, setCurrentPaymentLogs] = useState<any[]>([])
    const [currentPayment, setCurrentPayment] = useState<any>(null)
    const [showFinalizeDialog, setShowFinalizeDialog] = useState(false)
    const [showMarkAsPaidDialog, setShowMarkAsPaidDialog] = useState(false)
    const [showMarkAsUncollectableDialog, setShowMarkAsUncollectableDialog] = useState(false)
    const [showVoidDialog, setShowVoidDialog] = useState(false)
    const [showCapturePreauthorizedDialog, setShowCapturePreauthorizedDialog] = useState(false)
    const [showReleaseFundsDialog, setShowReleaseFundsDialog] = useState(false)
    const [showRefundDialog, setShowRefundDialog] = useState(false)
    const [refundAmount, setRefundAmount] = useState<number>(0)
    const [logDetailsDialog, setLogDetailsDialog] = useState(false)
    const [selectedLog, setSelectedLog] = useState<FolioLog | null>(null)

    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['details', 'payments', 'logs'], 'details');
    const setActiveTab = (newTab: string) => {
        setSearchParams({tab: newTab});
    };

    const folioData = useEntity({
        get: async () => {
            if (id && id !== 'new') {
                return FolioService.getById(id)
            } else {
                return {
                    status: FolioStatus.DRAFT
                }
            }
        },
        create: FolioService.create,
        update: FolioService.update,
        canDelete: item => item.status === FolioStatus.DRAFT,
        delete: (id) => FolioService.delete({id: [id]}),
        onDeleted: () => {
            navigate('/system/folios', {replace: true})
        },
        onSaved: (folio, wasCreateAction, prev) => {
            if (wasCreateAction) {
                navigate(`/system/folios/${folio.id}`, {replace: true})
            }
        },

    })

    const reservations = useReservations(folioData.item?.reservationId ? [folioData.item?.reservationId] : [])?.data
    const reservation = reservations?.[0]


    const folioItemsQuery = useQuery(['folioItems', id], async () => {
        if (id && id !== 'new') {
            const items = await FolioItemService.getAllFolioItems({
                folioIds: [id],
                limit: {
                    offset: 0,
                    limit: 1000,
                    sort: [{field: 'id', direction: 'ASC'}]
                },
            })
            return items.content || []
        } else {
            return []
        }
    })

    // Fetch payment methods for this profile
    const folioPaymentMethodsQuery = useQuery(
        ['folioPaymentMethods', id],
        async () => {
            if (!id || id == 'new') return [];

            const response = await PaymentMethodService.getAllPaymentMethods({
                folioIds: [id],
                limit: {
                    offset: 0,
                    limit: 100,
                }
            });

            return response.content || [];
        }
    );

    const folio = folioData.item
    const payments = [...(folio.payments || [])].reverse()
    const logs = [...(folio.logs || [])].reverse()
    const setFolio = folioData.setItem
    const folioItems = folioItemsQuery?.data || []
    const sortedFolioItems = [...folioItems].sort((a, b) => getFolioItemDescription(a).localeCompare(getFolioItemDescription(b)))
    const displayedFolioItems = sortedFolioItems.length ? sortedFolioItems : [{} as FolioItem]
    const folioItemColumnVisibility = {
        showDiscount: folioItems.some(hasFolioItemDiscount),
        showTax: folioItems.some(hasFolioItemTax),
    }
    const folioItemGridColumns = getFolioItemGridColumns(folioItemColumnVisibility)
    const canEditFolio = canUpdateFolio(folio)

    const totalQuery = useQuery(['folioTotal', id, folio.status], async () => {
        if (folio.status === FolioStatus.DRAFT) {
            return FolioService.calculateTotal({id: folio.id!})
        } else {
            return folio.total
        }
    })

    const totalFixedChargesNet = folioItems.filter(i => i.isFixedCharge && !i.inclusive).reduce((acc, item) => {
        return acc + (item.totalAmount?.amountBeforeTax || 0)
    }, 0)

    const totalFixedChargesGross = folioItems.filter(i => i.isFixedCharge && !i.inclusive).reduce((acc, item) => {
        return acc + (item.totalAmount?.amount || 0)
    }, 0)

    const totalReservationChargesNet = folioItems.filter(i => !i.isFixedCharge && !i.inclusive).reduce((acc, item) => {
        return acc + (item.totalAmount?.amountBeforeTax || 0)
    }, 0)

    const totalReservationChargesGross = folioItems.filter(i => !i.isFixedCharge && !i.inclusive).reduce((acc, item) => {
        return acc + (item.totalAmount?.amount || 0)
    }, 0)

    const formatTotalAmount = (value?: number) => `${(value ?? 0).toFixed(2)} ${totalQuery.data?.currency || '-'}`

    //endregion Sate

    //region Handlers

    const updateField = (field: keyof Folio, value: any) => {
        setFolio({...folio, [field]: value})
    }

    //endregion Handlers

    //region UI

    const hasFolioActions =
        canFinalizeFolio(folio) ||
        canMarkAsPaid(folio) ||
        canMarkAsUncollectable(folio) ||
        canVoidFolio(folio) ||
        canCapturePreauthorizedFolio(folio) ||
        canReleaseFunds(folio) ||
        canRefund(folio) ||
        folio.status === FolioStatus.OPEN

    let actionsSection = (
        <Stack gap={1} direction={'row'} justifyContent={'flex-start'}>
            {
                canFinalizeFolio(folio) && (
                    <Button
                        variant={'contained'}
                        onClick={() => setShowFinalizeDialog(true)}
                        startIcon={<CheckIcon/>}
                    >
                        {t('Finalize')}
                    </Button>
                )
            }

            {
                canMarkAsPaid(folio) && (
                    <Button
                        variant={'outlined'}
                        onClick={() => setShowMarkAsPaidDialog(true)}
                        startIcon={<PaidIcon/>}
                        disabled={readOnlyUser}
                    >
                        {t('Mark as Paid')}
                    </Button>
                )
            }

            {
                canMarkAsUncollectable(folio) && (
                    <Button
                        variant={'outlined'}
                        onClick={() => setShowMarkAsUncollectableDialog(true)}
                        startIcon={<GppBadIcon/>}
                        disabled={readOnlyUser}
                    >
                        {t('Mark as Uncollectable')}
                    </Button>
                )
            }

            {
                canVoidFolio(folio) && (
                    <Button
                        variant={'outlined'}
                        onClick={() => setShowVoidDialog(true)}
                        startIcon={<DeleteIcon/>}
                        disabled={readOnlyUser}
                    >
                        {t('Void folio')}
                    </Button>
                )
            }

            {
                canCapturePreauthorizedFolio(folio) && (
                    <Button
                        variant={'outlined'}
                        onClick={() => setShowCapturePreauthorizedDialog(true)}
                        startIcon={<PaymentIcon/>}
                    >
                        {t('Charge preauthorized')}
                    </Button>
                )
            }

            {
                canReleaseFunds(folio) && (
                    <Button
                        variant={'outlined'}
                        onClick={() => setShowReleaseFundsDialog(true)}
                        startIcon={<MoneyOffIcon/>}
                    >
                        {t('Release funds')}
                    </Button>
                )
            }

            {
                canRefund(folio) && (
                    <Button
                        variant={'outlined'}
                        onClick={() => {
                            setRefundAmount(totalQuery.data?.amount || 0)
                            setShowRefundDialog(true)
                        }}
                        startIcon={<UndoIcon/>}
                    >
                        {t('Refund')}
                    </Button>
                )
            }

            {folio.status === FolioStatus.OPEN && (
                <Button
                    variant={'contained'}
                    startIcon={<PaymentIcon/>}
                    onClick={() => setShowChargeDialog(true)}
                    disabled={readOnlyUser}
                >
                    {t('Charge')}
                </Button>
            )}
        </Stack>
    )

    if (folio.externalInfo) {
        actionsSection = (
            <Stack gap={1} direction={'row'} justifyContent={'flex-start'}>
                <Alert
                    variant={'outlined'}
                    severity={'warning'}
                    sx={{width: '100%', py: 0, lineHeight: 1, alignItems: 'center'}}
                >
                    {t('This folio is managed by an external system and thus cannot be edited here.')}
                </Alert>

            </Stack>
        )
    }

    const headerSection = (
        <SettingsEntityHeader
            title={folio.id ? `#${folio.id}` : undefined}
            data={folioData}
            back={() => navigate('/system/folios')}
            extraActions={
                folio.id && (
                    <Button
                        variant={'text'}
                        onClick={async () => {
                            if (folioData.isDirty) {
                                await folioData.save()
                            }
                            // Store the folio in Redux before opening print window
                            window.open(`/print/folio/${folio.id}`, '_blank')
                        }}
                        startIcon={<PrintIcon/>}
                    >
                        {t('Print')}
                    </Button>
                )
            }
        />
    )

    const basicInfoSection = (
        <FolioBasicInfo folio={folio} setFolio={setFolio} canEditFolio={canEditFolio} reservation={reservation}/>
    )


    const renderSummaryRow = (label: string, value: string, emphasized = false) => (
        <Box
            key={label}
            sx={{
                display: 'grid',
                gridTemplateColumns: folioItemGridColumns,
                columnGap: 1.5,
                alignItems: 'center',
                width: '100%',
            }}
        >
            <Typography
                variant={emphasized ? 'body1' : 'body2'}
                fontWeight={emphasized ? 600 : undefined}
                color={emphasized ? undefined : 'text.secondary'}
                sx={{
                    gridColumn: '1 / -2',
                    textAlign: 'right',
                }}
            >
                {label}
            </Typography>

            <Typography
                variant={emphasized ? 'body1' : 'body2'}
                fontWeight={emphasized ? 600 : undefined}
                sx={{
                    gridColumn: '-2 / -1',
                    textAlign: 'right',
                }}
            >
                {value}
            </Typography>
        </Box>
    )

    const folioItemsSection = (
        <Stack gap={2}>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
                <Typography variant={'h5'} textAlign={'start'}>
                    {t('Folio items')}
                </Typography>

                {
                    canEditFolioItems(folio) && (
                        <Button
                            variant={'contained'}
                            onClick={() => {
                                setCurrentFolioItem({
                                    quantity: 1,
                                    price: {
                                        currency: organization?.currencyCode
                                    },
                                    totalAmount: {
                                        currency: organization?.currencyCode
                                    }
                                })
                            }}
                            startIcon={<AddIcon/>}
                        >
                            {t('Add item')}
                        </Button>
                    )
                }
            </Stack>

            <Stack>
                <FolioItemsHeader {...folioItemColumnVisibility}/>

                {
                    displayedFolioItems.map((item, i) => (
                        <FolioItemRow
                            key={item.id || i}
                            item={item}
                            showDivider={i < displayedFolioItems.length - 1}
                            {...folioItemColumnVisibility}
                        />
                    ))
                }

                <Stack
                    gap={0.5}
                    sx={{
                        width: '100%',
                        pt: 3,
                    }}
                >
                    {
                        [
                            {label: t('Total reservation before tax'), value: formatTotalAmount(totalReservationChargesNet)},
                            {label: t('Total reservation'), value: formatTotalAmount(totalReservationChargesGross)},
                            {label: t('Total fixed charges before tax'), value: formatTotalAmount(totalFixedChargesNet)},
                            {label: t('Total fixed charges'), value: formatTotalAmount(totalFixedChargesGross)},
                        ].map(row => renderSummaryRow(row.label, row.value))
                    }

                    <Divider sx={{my: 1, width: '14rem', ml: 'auto'}}/>

                    {
                        [
                            {label: t('Total before tax'), value: formatTotalAmount(totalQuery.data?.amountBeforeTax)},
                            {label: t('Total'), value: formatTotalAmount(totalQuery.data?.amount)},
                        ].map(row => renderSummaryRow(row.label, row.value, true))
                    }
                </Stack>
            </Stack>

        </Stack>
    )

    const folioPaymentsSection = (
        <>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={4}>
                <Typography variant={'h5'} textAlign={'start'}>
                    {t('Folio payments')}
                </Typography>
            </Stack>

            <Stack gap={2}>
                {payments.map((payment, i) => {
                    return (
                        <Card
                            onClick={(e) => {
                                e.stopPropagation();
                                setCurrentPayment(payment);
                                setShowLogsDialog(true);
                            }}
                            sx={{
                                cursor: 'pointer',
                                gap: 1,
                                p: 1,
                                '&:hover': {
                                    boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)',
                                }
                            }}
                            elevation={1}
                        >
                            <Stack
                                key={i}
                                sx={{
                                    width: '100%',
                                    gap: 1
                                }}
                            >
                                <Typography sx={{flex: 1}}>
                                    {t('Type')}: {payment.paymentType} ({payment.provider})
                                </Typography>

                                <Typography sx={{minWidth: 80}}>
                                    {t('Amount')}: {payment.amount?.amount || 0}{payment.amount?.isPercentage ? '%' : ''} {payment.amount?.currency || '-'}
                                </Typography>

                                {
                                    !!payment.deadlineDay &&
                                    <Typography sx={{minWidth: 80}}>
                                        {t('Deadline')}: {payment.deadlineDay}
                                    </Typography>
                                }

                                {
                                    !!payment.transactionDate &&
                                    <Typography sx={{minWidth: 80}}>
                                        {t('Transaction date')}: {payment.transactionDate}
                                    </Typography>
                                }

                                {
                                    !!payment.transactionId &&
                                    <Typography sx={{minWidth: 80}}>
                                        {t('Transaction ID')}: {payment.transactionId}
                                    </Typography>
                                }

                                <Typography sx={{minWidth: 80}}>
                                    {t('Status')}: {payment.status}
                                </Typography>

                            </Stack>
                        </Card>
                    )
                })}

                {payments.length === 0 && (
                    <Typography>{t('No payments found')}</Typography>
                )}
            </Stack>
        </>
    )

    //endregion UI

    const folioPaymentMethodsSection = (
        <>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Payment Methods')}
            </Typography>

            <Stack gap={2}>
                {folioPaymentMethodsQuery.data?.map((paymentMethod, i) => (
                    <Card
                        onClick={(e) => {
                            window.open(`/system/paymentMethods/${paymentMethod.id}`, '_blank')
                        }}
                        sx={{
                            cursor: 'pointer',
                            gap: 1,
                            p: 1,
                            '&:hover': {
                                boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)',
                            }
                        }}
                        elevation={1}
                    >
                        <Stack
                            key={i}
                            sx={{
                                width: '100%',
                                gap: 1
                            }}
                        >
                            <Typography sx={{flex: 1}}>
                                {t('Provider')}: {paymentMethod.provider}
                            </Typography>

                            <Typography sx={{minWidth: 80}}>
                                {t('Type')}: {paymentMethod.type}
                            </Typography>

                            {paymentMethod.card && (
                                <>
                                    <Typography sx={{minWidth: 80}}>
                                        {t('Card')}: {paymentMethod.card.brand} •••• {paymentMethod.card.last4}
                                    </Typography>

                                    <Typography sx={{minWidth: 80}}>
                                        {t('Expires')}: {paymentMethod.card.expMonth}/{paymentMethod.card.expYear}
                                    </Typography>
                                </>
                            )}

                            {paymentMethod.billingDetails && (
                                <>
                                    {paymentMethod.billingDetails.name && (
                                        <Typography sx={{minWidth: 80}}>
                                            {t('Name')}: {paymentMethod.billingDetails.name}
                                        </Typography>
                                    )}

                                    {paymentMethod.billingDetails.email && (
                                        <Typography sx={{minWidth: 80}}>
                                            {t('Email')}: {paymentMethod.billingDetails.email}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Stack>
                    </Card>
                ))}

                {folioPaymentMethodsQuery.isLoading && (
                    <Typography>{t('Loading payment methods...')}</Typography>
                )}

                {!folioPaymentMethodsQuery.isLoading && folioPaymentMethodsQuery.data?.length === 0 && (
                    <Typography>{t('No payment methods found')}</Typography>
                )}
            </Stack>
        </>
    )


    const logsSection = (
        <Stack gap={2}>
            {logs?.length ? (
                <Box sx={{width: '100%'}}>
                    <Box sx={{overflow: 'auto'}}>
                        <Stack spacing={1}>
                            <Stack
                                direction="row"
                                spacing={2}
                                sx={{
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    pb: 1
                                }}
                            >
                                <Typography variant="subtitle2" sx={{flex: "0 0 200px"}}>
                                    {t('Date')}
                                </Typography>
                                <Typography variant="subtitle2" sx={{flex: 1}}>
                                    {t('Message')}
                                </Typography>
                            </Stack>

                            {logs.map((log, index) => (
                                <Stack
                                    key={index}
                                    direction="row"
                                    spacing={2}
                                    sx={{
                                        py: 1,
                                        borderBottom: index < folio.logs!.length - 1 ? '1px solid rgba(224, 224, 224, 0.5)' : 'none',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: 'action.hover'
                                        }
                                    }}
                                    onClick={() => {
                                        setSelectedLog(log);
                                        setLogDetailsDialog(true);
                                    }}
                                >
                                    <Typography variant="body2" sx={{flex: "0 0 200px", color: 'text.secondary'}}>
                                        {formatDateHM(log.createdAt) || '-'}
                                    </Typography>
                                    <Typography variant="body2" sx={{flex: 1}}>
                                        {log.message || '-'}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </Box>
                </Box>
            ) : (
                <Typography variant="body1">{t('No logs available')}</Typography>
            )}
        </Stack>
    );

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete folio')}
                description={t('This permanently deletes the folio. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                disabled={!folioData.canDelete}
                loading={folioData.saving}
                onDelete={() => folioData.removeNow()}
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {folio.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const folioDataUI = (
        <Stack
            sx={{
                width: '100%',
                maxWidth: '900px',
                mx: 'auto',
                mt: 3,
                gap: 6,
            }}
        >
            {(folio.externalInfo || hasFolioActions) && actionsSection}

            <TabContext value={activeTab}>
                <ScrollableTabList onChange={(e, v) => setActiveTab(v)}>
                    <Tab label={t('Details')} value={'details'}/>
                    <Tab label={t('Payments')} value={'payments'}/>
                    <Tab label={t('Logs')} value={'logs'}/>
                </ScrollableTabList>
            </TabContext>

            {activeTab === 'details' &&
                <Stack gap={6}>
                    {basicInfoSection}
                    <Divider/>
                    {folioItemsSection}
                    {!!folio.id && (
                        <>
                            <Divider/>
                            {dangerZoneSection}
                        </>
                    )}
                </Stack>
            }

            {activeTab === 'payments' &&
                <Stack gap={6}>
                    {folioPaymentMethodsSection}
                    <Divider/>
                    {folioPaymentsSection}
                </Stack>
            }

            {activeTab === 'logs' && logsSection}
        </Stack>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}
            {folioDataUI}

            <FolioItemDialog
                open={!!currentFolioItem}
                onClose={() => setCurrentFolioItem(undefined)}
                onSetFolioItem={(item) => {
                    setCurrentFolioItem(undefined)
                    folioItemsQuery.refetch()
                    totalQuery.refetch()
                }}
                folioItem={currentFolioItem}
                editable={canEditFolioItems(folio)}
                folio={folio}
            />

            <FolioChargeDialog
                open={showChargeDialog}
                onClose={() => setShowChargeDialog(false)}
                folio={folio}
                total={totalQuery.data}
                onSuccess={() => {
                    folioData.fetch()
                }}
                onFail={() => {
                    folioData.fetch()
                }}
            />

            <FolioPaymentDetailsDialog
                open={showLogsDialog}
                onClose={() => setShowLogsDialog(false)}
                payment={currentPayment}
            />

            {/* Confirmation Dialogs */}
            <Dialog
                open={showFinalizeDialog}
                onClose={() => setShowFinalizeDialog(false)}
            >
                <DialogTitle>{t('Finalize Folio')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Are you sure you want to finalize this folio? Once finalized, no further items can be added.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowFinalizeDialog(false)}>{t('No')}</Button>
                    <Button
                        onClick={async () => {
                            if (folioData.isDirty) {
                                await folioData.save()
                            }
                            await FolioService.finalize({id: folio.id!})
                            folioData.fetch()
                            setShowFinalizeDialog(false)
                        }}
                        autoFocus
                    >
                        {t('Yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showMarkAsPaidDialog}
                onClose={() => setShowMarkAsPaidDialog(false)}
            >
                <DialogTitle>{t('Mark as Paid')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Are you sure you want to mark this folio as paid? This will change the folio status to PAID.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowMarkAsPaidDialog(false)}>{t('No')}</Button>
                    <Button
                        onClick={async () => {
                            await FolioService.markAsPaid({id: folio.id!})
                            folioData.fetch()
                            setShowMarkAsPaidDialog(false)
                        }}
                        autoFocus
                    >
                        {t('Yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showMarkAsUncollectableDialog}
                onClose={() => setShowMarkAsUncollectableDialog(false)}
            >
                <DialogTitle>{t('Mark as Uncollectable')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Are you sure you want to mark this folio as uncollectable? This action cannot be undone.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowMarkAsUncollectableDialog(false)}>{t('No')}</Button>
                    <Button
                        onClick={async () => {
                            await FolioService.markAsUncollectable({id: folio.id!})
                            folioData.fetch()
                            setShowMarkAsUncollectableDialog(false)
                        }}
                        autoFocus
                    >
                        {t('Yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showVoidDialog}
                onClose={() => setShowVoidDialog(false)}
            >
                <DialogTitle>{t('Void Folio')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Are you sure you want to void this folio? This action cannot be undone.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowVoidDialog(false)}>{t('No')}</Button>
                    <Button
                        onClick={async () => {
                            await FolioService.void({id: folio.id!})
                            folioData.fetch()
                            setShowVoidDialog(false)
                        }}
                        autoFocus
                    >
                        {t('Yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showCapturePreauthorizedDialog}
                onClose={() => setShowCapturePreauthorizedDialog(false)}
            >
                <DialogTitle>{t('Capture preauthorized amount')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Are you sure you want to capture preauthorized payment?')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCapturePreauthorizedDialog(false)}>{t('No')}</Button>
                    <Button
                        onClick={async () => {
                            try {
                                const response = await FolioService.capturePreauthorized({id: folio.id!})
                                if (response.status === FolioStatus.PAID) {
                                    alert('Payment captured successfully')
                                } else {
                                    alert('Payment not captured')
                                }
                            } catch (e) {
                                console.error(e)
                                alert("Error capturing preauthorized payment")
                            }
                            folioData.fetch()
                            setShowCapturePreauthorizedDialog(false)
                        }}
                        autoFocus
                    >
                        {t('Yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Release Funds Dialog */}
            <Dialog
                open={showReleaseFundsDialog}
                onClose={() => setShowReleaseFundsDialog(false)}
            >
                <DialogTitle>{t('Release Funds')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('Are you sure you want to release the preauthorized funds?')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowReleaseFundsDialog(false)}>{t('No')}</Button>
                    <Button
                        onClick={async () => {
                            try {
                                const response = await FolioService.reverse({id: folio.id!})
                                if (response.status === FolioStatus.RELEASED) {
                                    alert('Funds released successfully')
                                } else {
                                    alert('Funds release completed')
                                }
                                folioData.fetch()
                            } catch (e) {
                                console.error(e)
                                alert("Error releasing funds")
                            }
                            setShowReleaseFundsDialog(false)
                        }}
                        autoFocus
                    >
                        {t('Yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Refund Dialog */}
            <Dialog
                open={showRefundDialog}
                onClose={() => setShowRefundDialog(false)}
            >
                <DialogTitle>{t('Refund')}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{mb: 2}}>
                        {t('Enter the amount to refund', {amount: (totalQuery?.data?.amount || 0) + '' + (totalQuery.data?.currency || 'EUR')})}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('Refund Amount')}
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={refundAmount}
                        onChange={(e) => setRefundAmount(Math.max(0, Math.min(totalQuery.data?.amount || 0, parseFloat(e.target.value) || 0)))}
                        InputProps={{
                            endAdornment: <Typography>{totalQuery.data?.currency || 'EUR'}</Typography>,
                            inputProps: {
                                min: 0,
                                max: totalQuery.data?.amount || 0,
                                step: 0.01
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowRefundDialog(false)}>{t('Cancel')}</Button>
                    <Button
                        onClick={async () => {
                            try {
                                const amountCents = Math.round(refundAmount * 100)
                                const response = await FolioService.refund({id: folio.id!, amountCents})
                                if (response.status === FolioStatus.REFUNDED) {
                                    alert('Refund processed successfully')
                                } else {
                                    alert('Refund completed')
                                }
                                folioData.fetch()
                            } catch (e) {
                                console.error(e)
                                alert("Error processing refund")
                            }
                            setShowRefundDialog(false)
                        }}
                        disabled={refundAmount <= 0}
                        variant="contained"
                    >
                        {t('Refund')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Log Details Dialog */}
            <Dialog
                open={logDetailsDialog}
                onClose={() => setLogDetailsDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>{t('Log Details')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Property title={t('Date')}>
                            <Typography variant="body1">
                                {selectedLog?.createdAt?.toLocaleString() || '-'}
                            </Typography>
                        </Property>

                        <Property title={t('Message')}>
                            <Typography variant="body1">
                                {selectedLog?.message || '-'}
                            </Typography>
                        </Property>

                        {selectedLog?.content && (
                            <Property title={t('Content')}>
                                <Box
                                    sx={{
                                        maxHeight: '400px',
                                        overflow: 'auto',
                                        bgcolor: 'grey.100',
                                        p: 2,
                                        borderRadius: 1
                                    }}
                                >
                                    <pre style={{
                                        margin: 0,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}>
                                        {(() => {
                                            try {
                                                // Try to parse and format as JSON
                                                const parsedContent = JSON.parse(selectedLog.content);
                                                return JSON.stringify(parsedContent, null, 2);
                                            } catch (e) {
                                                // If not valid JSON, return as is
                                                return selectedLog.content;
                                            }
                                        })()}
                                    </pre>
                                </Box>
                            </Property>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLogDetailsDialog(false)}>{t('Close')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default FolioDetails;
